import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  ButtonIconWithLabel,
  Icon_Briefcase24,
  Icon_Checkmark24,
  Icon_Disconnect24,
  Icon_Globe24,
  Icon_Lock24,
  Menu,
  Modal,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { ReactComponent as Icon_Briefcase } from "../../../../assets/images/briefcase.svg";
import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../../../config";
import { useShareModalContext } from "../../../../contexts/ShareModalFormContext";
import {
  ProjectViewLinkSpaceTypes,
  useMutateProjectViewLink,
  useProjectViewLinkSharing,
} from "../../../../hooks/useProjectViewLink";
import {
  useWebExport,
  type WebExportBaseRole,
  WebExportBaseRoles,
  type WebExportSharedState,
  WebExportSharedStates,
  WebExportUserRolesDisplayNames,
} from "../../../../hooks/useWebExport";
import { ElementClickAction, ElementClickLocation } from "../../../../lib/TrackingAdapter";
import { SearchParam, SearchParamIntent } from "../../../../types";
import useTracker from "../../../../utils/useTracker";
import { Avatar } from "../../../Comment/Avatar";
import { DoubleIconLabel } from "./components/DoubleIconLabel";
import { EmailInvitationFormViewLink } from "./components/EmailInvitationFormViewLink";
import { Icon_FillerSvg24 } from "./components/FillerSvg";
import { InvitationRowViewLink } from "./components/InvitationRowViewLink";
import { MemberRowViewLink } from "./components/MemberRowViewLink";
import { MenuPortal, OverlayProvider } from "./components/MenuPortal";
import { SelectActivator } from "./components/SelectActivator";
import { Separator } from "./components/Separator";
import { SingleIconLabel } from "./components/SingleIconLable";

import style from "./ShareViewLinkModal.module.css";

const generalAccessOptions: Readonly<{
  [key in Exclude<WebExportSharedState, "not_shared">]: { icon: ReactNode; text: string };
}> = {
  anyone_with_link: { icon: <Icon_Globe24 />, text: "Anyone with the link" },
  team_only: { icon: <Icon_Briefcase24 />, text: "Members of this team" },
  restricted: { icon: <Icon_Lock24 />, text: "Only the people invited" },
} as const;

const baseRoleOptions: { [key in WebExportBaseRole]: string } = {
  viewer: WebExportUserRolesDisplayNames["viewer"],
  commenter: WebExportUserRolesDisplayNames["commenter"],
} as const;

const schema = z.object({
  generalAccess: z.union([
    z.literal(WebExportSharedStates.AnyoneWithLink),
    z.literal(WebExportSharedStates.TeamOnly),
    z.literal(WebExportSharedStates.Restricted),
  ]),
  baseRole: z.union([
    z.literal(WebExportBaseRoles.Viewer),
    z.literal(WebExportBaseRoles.Commenter),
  ]),
});
type ShareModalFormData = z.infer<typeof schema>;

export const ShareViewLinkModal: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareModalOpen, setShareModalOpen] = useState(
    searchParams.get(SearchParam.Intent) === SearchParamIntent.Share
  );
  const [copySuccessVisible, setCopySuccessVisible] = useState(false);
  const { data: webExportData } = useWebExport();
  const tracker = useTracker();
  const { viewLink = "" } = useParams();
  const {
    data: projectViewLinkSharingData,
    refetch: refetchProjectViewLinkSharing,
    isRefetching: isProjectViewLinkSharingRefetching,
  } = useProjectViewLinkSharing();

  const { isPending, setIsPending } = useShareModalContext();

  const consumeShareIntent = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        searchParams.delete(SearchParam.Intent);
        return searchParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const formRef = useRef<HTMLFormElement>(null);
  const emailListContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsPending("refetching", isProjectViewLinkSharingRefetching);
  }, [isProjectViewLinkSharingRefetching, setIsPending]);

  const { handleSubmit, setValue, setError, reset, control, formState, clearErrors } =
    useForm<ShareModalFormData>({
      mode: "onChange",
      defaultValues: {
        generalAccess: projectViewLinkSharingData?.sharedState as Exclude<
          WebExportSharedState,
          "not_shared"
        >,
        baseRole: projectViewLinkSharingData?.baseRole as WebExportBaseRole,
      },
      resolver: zodResolver(schema),
    });

  useEffect(() => {
    setValue(
      "generalAccess",
      projectViewLinkSharingData?.sharedState as Exclude<WebExportSharedState, "not_shared">
    );
  }, [projectViewLinkSharingData?.sharedState, setValue]);

  useEffect(() => {
    setValue("baseRole", projectViewLinkSharingData?.baseRole as WebExportBaseRole);
  }, [projectViewLinkSharingData?.baseRole, setValue]);

  const resetForm = useCallback(() => {
    reset(
      {
        generalAccess: projectViewLinkSharingData?.sharedState as Exclude<
          WebExportSharedState,
          "not_shared"
        >,
        baseRole: projectViewLinkSharingData?.baseRole as WebExportBaseRole,
      },
      { keepErrors: true }
    );
  }, [reset, projectViewLinkSharingData]);

  const { mutateAsync: mutateProjectViewLink, isPending: isMutateProjectViewLinkPending } =
    useMutateProjectViewLink({
      onError: () => {
        setError("generalAccess", { message: "Something went wrong. Please try again." });
        resetForm();
      },
      onSuccess: ({ sharedState, baseRole }) => {
        if (sharedState !== projectViewLinkSharingData?.sharedState) {
          tracker.trackShareModalGeneralPermissionChanged({
            newValue: sharedState,
            viewLinkId: viewLink,
            webExportId: webExportData?.webExport.webExportId,
            oldValue: projectViewLinkSharingData?.sharedState ?? "",
          });
        }

        if (baseRole && baseRole !== projectViewLinkSharingData?.baseRole) {
          tracker.trackShareModalGeneralRoleChanged({
            newValue: baseRole,
            viewLinkId: viewLink,
            webExportId: webExportData?.webExport.webExportId,
            oldValue: projectViewLinkSharingData?.baseRole ?? "",
          });
        }
      },
    });

  useEffect(() => {
    setIsPending("general_access", isMutateProjectViewLinkPending);
  }, [setIsPending, isMutateProjectViewLinkPending]);

  const onSubmit = useCallback(
    async (data: ShareModalFormData) => {
      try {
        await mutateProjectViewLink({
          viewLinkId: viewLink,
          body: {
            sharedState: data.generalAccess,
            baseRole: data.baseRole,
          },
        });
      } catch {
        // This is not to throw unhandled promise rejection error
      }
    },
    [mutateProjectViewLink, viewLink]
  );

  const generalAccessUseWatch = useWatch({ name: "generalAccess", control });
  const baseRoleUseWatch = useWatch({ name: "baseRole", control });

  const onClose = useCallback(() => {
    consumeShareIntent();
    resetForm();
    clearErrors();
    setShareModalOpen(false);
    tracker.trackShareModalVisibilityChanged({
      viewLinkId: viewLink,
      webExportId: webExportData?.webExport.webExportId,
      visible: false,
    });
  }, [resetForm, clearErrors, tracker, viewLink, consumeShareIntent, webExportData]);

  const owner = useMemo(() => {
    const isPrivateSpace = projectViewLinkSharingData?.space.spaceType === "private";
    const ownerUser = isPrivateSpace
      ? projectViewLinkSharingData?.space.spaceUsers?.[0]
      : undefined;
    const defaultSpaceName = isPrivateSpace ? defaultPrivateSpaceName : defaultTeamSpaceName;
    const name =
      ownerUser?.name ||
      ownerUser?.email ||
      projectViewLinkSharingData?.space.spaceName ||
      defaultSpaceName;
    const displayName = ownerUser ? name : `Anyone in ${name}`;

    return { name, displayName };
  }, [projectViewLinkSharingData?.space]);

  return (
    <>
      <Button
        onClick={() => {
          refetchProjectViewLinkSharing();
          setShareModalOpen(true);
          tracker.trackShareModalVisibilityChanged({
            viewLinkId: viewLink,
            webExportId: webExportData?.webExport.webExportId,
            visible: true,
          });
        }}
      >
        Share
      </Button>
      <Modal
        className={style.modal}
        open={shareModalOpen}
        onClose={onClose}
        title="Share this link"
        persistent
      >
        <OverlayProvider>
          <div className={style.container}>
            {projectViewLinkSharingData && owner.name && (
              <div>
                <span className={style.sectionTitle}>People with access</span>
                <EmailInvitationFormViewLink />
                <div ref={emailListContainerRef} className={style.emailListContainer}>
                  <div className={style.spaceContainer}>
                    {projectViewLinkSharingData.space.spaceType ===
                      ProjectViewLinkSpaceTypes.Private && <Avatar title={owner.name} />}
                    {projectViewLinkSharingData.space.spaceType ===
                      ProjectViewLinkSpaceTypes.Team && (
                      <div className={style.spaceLogo}>
                        <Icon_Briefcase />
                      </div>
                    )}
                    <span>
                      <b>{owner.displayName}</b>
                    </span>
                    <span>{WebExportUserRolesDisplayNames["owner"]}</span>
                  </div>

                  {projectViewLinkSharingData.members.map((member) => (
                    <Fragment key={member.userId}>
                      <Separator />
                      <MemberRowViewLink member={member} />
                    </Fragment>
                  ))}
                  {projectViewLinkSharingData.invitations.map((invitation) => (
                    <Fragment key={invitation.invitationId}>
                      <Separator />

                      <InvitationRowViewLink invitation={invitation} />
                    </Fragment>
                  ))}

                  <Separator className={classNames(style.overflowSeparator, style.bottom)} />
                </div>
              </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
              <span className={style.sectionTitle}>General access</span>
              <div className={style.generalAccessContainer}>
                <div>
                  <MenuPortal
                    dropdownPosition="right"
                    activator={
                      <SelectActivator
                        aria-label="General access"
                        icon={generalAccessOptions[generalAccessUseWatch]?.icon}
                        label={generalAccessOptions[generalAccessUseWatch]?.text}
                        disabled={isPending}
                        className={style.bold}
                      />
                    }
                  >
                    {Object.entries(generalAccessOptions).map(([key, { icon, text }]) => (
                      <Menu.Item
                        key={key}
                        closeOnClick
                        onClick={(event) => {
                          event.preventDefault();

                          if (generalAccessUseWatch === key) {
                            return;
                          }

                          setValue("generalAccess", key);
                          formRef.current?.requestSubmit();
                        }}
                      >
                        <DoubleIconLabel
                          icon1={generalAccessUseWatch === key ? <Icon_Checkmark24 /> : null}
                          icon2={icon}
                          label={text}
                        />
                      </Menu.Item>
                    ))}
                  </MenuPortal>
                  {formState.errors.generalAccess?.message && (
                    <span className={style.errorMessage}>
                      {formState.errors.generalAccess?.message}
                    </span>
                  )}
                </div>

                {generalAccessUseWatch !== WebExportSharedStates.Restricted && (
                  <div>
                    <MenuPortal
                      dropdownPosition="left"
                      activator={
                        <SelectActivator
                          aria-label="Base role"
                          label={baseRoleOptions[baseRoleUseWatch]}
                          disabled={isPending}
                        />
                      }
                    >
                      {Object.entries(baseRoleOptions).map(([key, label]) => (
                        <Menu.Item
                          key={key}
                          closeOnClick
                          onClick={(event) => {
                            event.preventDefault();

                            if (baseRoleUseWatch === key) {
                              return;
                            }

                            setValue("baseRole", key);
                            formRef.current?.requestSubmit();
                          }}
                        >
                          <SingleIconLabel
                            icon={
                              baseRoleUseWatch === key ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />
                            }
                            label={label}
                          />
                        </Menu.Item>
                      ))}
                    </MenuPortal>
                  </div>
                )}
              </div>
            </form>

            <div className={style.footerContainer}>
              <div className={style.copyLinkContainer}>
                <Button
                  variant="grayscale"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${location.pathname}`);
                    setCopySuccessVisible(true);
                    setTimeout(() => {
                      setCopySuccessVisible(false);
                    }, 1000);
                    tracker.trackButtonClick({
                      action: ElementClickAction.COPY_LINK,
                      location: ElementClickLocation.SHARE_MODAL,
                    });
                  }}
                >
                  <ButtonIconWithLabel icon={<Icon_Disconnect24 />} label="Copy link" />
                </Button>
                <div
                  className={classNames(
                    style.copySuccessContainer,
                    copySuccessVisible && style.visible
                  )}
                >
                  <Icon_Checkmark24 />
                  <span>Copied!</span>
                </div>
              </div>
              <Button onClick={onClose} loading={isPending}>
                Done
              </Button>
            </div>
          </div>
        </OverlayProvider>
      </Modal>
    </>
  );
};
